import { useAuth } from '@/features/auth/useAuth';
import { type ComponentType, Suspense, lazy, useCallback, useEffect, useState } from 'react';
import type { RevealProps } from './NewDesignReveal';
import { OldLoadingCover } from './OldLoadingCover';
import { REVEAL_PARAM, markRevealed, revealFor } from './revealRules';

/**
 * Bezpiecznik: jeśli moduł albo zrzut nie wczyta się w tym czasie (zawieszone
 * żądanie, słaby zasięg), rozbiórki nie ma — nikt nie może utknąć przed CRM.
 */
const LOAD_TIMEOUT_MS = 8000;

/** Gdy moduł rozbiórki się nie wczyta (np. świeży deploy) — pomijamy ją, strona działa dalej. */
function SkipReveal({ onDone }: RevealProps) {
  useEffect(() => onDone(), [onDone]);
  return null;
}

const NewDesignReveal = lazy<ComponentType<RevealProps>>(() =>
  import('./NewDesignReveal')
    .then((m) => ({ default: m.NewDesignReveal }))
    .catch(() => ({ default: SkipReveal })),
);

function dropRevealParam(): void {
  const url = new URL(window.location.href);
  if (!url.searchParams.has(REVEAL_PARAM)) return;
  url.searchParams.delete(REVEAL_PARAM);
  window.history.replaceState(window.history.state, '', url);
}

/** Rozbiórka starego wyglądu nad świeżo załadowanym CRM (patrz revealRules.ts). */
export function RevealGate() {
  const { user } = useAuth();
  const [person] = useState(() => (user ? revealFor(user, window.location.search) : null));
  const [done, setDone] = useState(false);
  const [ready, setReady] = useState(false);
  const userId = user?.id;

  // „Zaliczone” zapisujemy przy 10. uderzeniu i przy każdym końcu (także awarii) —
  // odświeżenie nigdy nie wrzuca z powrotem w zawieszoną rozbiórkę.
  const commit = useCallback(() => {
    if (userId) markRevealed(userId);
  }, [userId]);

  const finish = useCallback(() => {
    commit();
    dropRevealParam();
    setDone(true);
  }, [commit]);

  const onReady = useCallback(() => setReady(true), []);

  useEffect(() => {
    if (!person || done || ready) return;
    const timer = window.setTimeout(finish, LOAD_TIMEOUT_MS);
    return () => window.clearTimeout(timer);
  }, [person, done, ready, finish]);

  if (!person || done) return null;
  return (
    <Suspense
      fallback={<OldLoadingCover dark={document.documentElement.classList.contains('dark')} />}
    >
      <NewDesignReveal person={person} onDone={finish} onReady={onReady} onCommit={commit} />
    </Suspense>
  );
}
